import type { CSSProperties } from 'react';
import type { CoverPageContent } from '@/blocks/domain/types/BlockContent/CoverPageContent';
import type { RendererProps } from '../../domain/RendererContract';
import { blockStyleToCss } from '../../application/blockStyleToCss';
import { resolveTokens } from '../../application/resolveTokens';
import {
  isCoverPreviewUnbound,
  readCoverImageFromContext,
  resolveCoverCustomerName,
  resolveCoverDate
} from '../../application/documentBranding';
import { resolveCoverLogo } from '../../application/coverLogo';
import { COVER_PAGE_LAYOUT_PX } from '../../application/coverPageLayout';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { CompanyLogoSlot } from '../CompanyLogoSlot';
import { CoverImageLayer } from '../CoverImageLayer';

/**
 * Cover layout: the company logo is pinned to the TOP of the cover (at its stored alignment), and
 * the title / customer / date lines form one group that takes the remaining height and is
 * vertically centred inside it. With no logo the group fills the whole cover, so the text sits
 * exactly where the former single centred stack put it.
 */
export function CoverPageRenderImpl({ content, style, context, knownPaths }: RendererProps<CoverPageContent>) {
  const resolveOptions = knownPaths ? { knownPaths } : undefined;
  const defaultMinHeight = COVER_PAGE_LAYOUT_PX.minHeight;
  const coverImage = readCoverImageFromContext(context);
  const wrapperStyle: CSSProperties = {
    ...blockStyleToCss(style),
    display: 'flex',
    flexDirection: 'column',
    // Stretch full page width so logoAlignment (left/center/right) is visible.
    // Parent PageRender is a column flex; grow to fill the first sheet body.
    alignItems: 'stretch',
    alignSelf: 'stretch',
    flex: 1,
    width: '100%',
    // The logo starts at the top; the text group below fills the rest and centres its own lines.
    justifyContent: 'flex-start',
    textAlign: 'center',
    gap: COVER_PAGE_LAYOUT_PX.gap,
    minHeight: typeof style.previewHeight === 'number' && style.previewHeight > 0 ? style.previewHeight : defaultMinHeight,
    boxSizing: 'border-box',
    // A stacking context, so the cover image layer (z-index -1) paints above this block's
    // background and below its text.
    ...(coverImage ? { position: 'relative', isolation: 'isolate' } : {})
  };
  const textGroupStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    gap: COVER_PAGE_LAYOUT_PX.gap
  };

  const logo = resolveCoverLogo(content.showLogo, context);
  // Unbound sample context: leave title tokens as authored (do not fill Acme / em-dash).
  const title = isCoverPreviewUnbound(context)
    ? (content.title ?? '').trim()
    : resolveTokens(content.title ?? '', context, resolveOptions).trim();
  const customerName = resolveCoverCustomerName(context);
  const dateValue = resolveCoverDate(context);
  const showText = content.showText !== false;
  const showTitle = showText && Boolean(title);
  const showCustomer = showText && content.showCustomerName && Boolean(customerName);
  const showDate = showText && content.showDate && Boolean(dateValue);

  return (
    <section aria-label="block-cover_page" style={wrapperStyle}>
      {coverImage ? <CoverImageLayer image={coverImage} /> : null}
      {logo.url ? <CompanyLogoSlot url={logo.url} size={logo.size} alignment={logo.alignment} slot="cover" /> : null}
      {showTitle || showCustomer || showDate ? (
        <div data-testid="document-render:cover:text-group" style={textGroupStyle}>
          {showTitle ? (
            <div
              style={{
                fontSize: Math.max(style.fontSize, COVER_PAGE_LAYOUT_PX.titleMinFontSize),
                fontWeight: 700,
                color: style.textColor || TEXT_COLORS.TEXT_PRIMARY
              }}
            >
              {title}
            </div>
          ) : null}
          {showCustomer ? (
            <div style={{ fontSize: Math.max(style.fontSize, COVER_PAGE_LAYOUT_PX.customerFontSize), color: TEXT_COLORS.MUTED }}>
              {customerName}
            </div>
          ) : null}
          {showDate ? <div style={{ fontSize: COVER_PAGE_LAYOUT_PX.dateFontSize, color: TEXT_COLORS.MUTED }}>{dateValue}</div> : null}
        </div>
      ) : null}
    </section>
  );
}

export const renderer = {
  Component: CoverPageRenderImpl,
  flowBehavior: 'atomic' as const,
  pageBreakAfter: true
};

export type { CoverPageContent };
